// src/services/eventsServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/events`;

/* Helpers */
export const toAbsoluteEventImage = (u = "") => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s)) return s; // déjà absolue
  if (s.startsWith("//")) return window.location.protocol + s; // protocole courant
  if (s.startsWith("/")) return `${window.location.origin}${s}`; // chemin site
  // ex: "uploads/x.jpg" ou "./uploads/x.jpg"
  return `${window.location.origin}/${s.replace(/^\.?\/*/, "")}`;
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      const joined = v
        .map((x) => String(x).trim())
        .filter(Boolean)
        .join(",");
      if (joined) usp.append(k, joined);
    } else if (typeof v === "boolean") {
      usp.append(k, v ? "true" : "false");
    } else {
      usp.append(k, String(v));
    }
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

/* ========= Public ========= */

// Tous les événements actifs
export const getEvents = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getAllEvents`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des événements :", error);
    return [];
  }
};

// 3 prochains (home)
export const getNextThreeEvents = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getNextThreeEvents`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      "Erreur lors du chargement des prochains événements :",
      error
    );
    return [];
  }
};

// Détail
export const getEventById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur lors du chargement de l’événement :", error);
    return null;
  }
};

/* ========= Admin ========= */

// Recherche / pagination
// params: { q, categorie, is_active, date_from, date_to, page=1, limit=24, sort='start_date'|'created_at'|'title', dir='ASC'|'DESC' }
export const searchEvents = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 24,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (error) {
    console.error("Erreur recherche événements :", error);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

// Création
// payload: { title*, subtitle?, categorie?, description?, start_date*, end_date?, photo_vignette?, external_link?, is_active? }
export const createEvent = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur création événement :", error);
    return null;
  }
};

// Remplacement (PUT)
export const updateEvent = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur mise à jour événement :", error);
    return null;
  }
};

// Mise à jour partielle (PATCH)
export const patchEvent = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur patch événement :", error);
    return null;
  }
};

// Activer / désactiver
export const toggleEventActive = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}/toggle`, null, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur toggle événement :", error);
    return null;
  }
};

// Suppression
export const deleteEvent = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return true;
  } catch (error) {
    console.error("Erreur suppression événement :", error);
    return false;
  }
};
